import { Dialog } from '@angular/cdk/dialog';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { NavigationStart, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { IActionNavItemModel } from '@layout/models/theme/nav-item.model';
import { CdnRegisteryService } from '@layout/services/cdn-registery.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { catchError, filter, first, map, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { GlobalAuthService } from 'src/app/core/auth/services/global-auth.service';
import { RolesEnum } from 'src/app/core/auth/enums/roles-enum';
import { ISiteModel } from 'src/app/core/auth/models/user-site.model';
import { OfficeUserService } from '@feature/office/services/office-user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OfficeUser } from '@feature/office/models/office-user-model';
import { RolesService } from 'src/app/core/auth/services/roles.service';
import { UserGuideDialogComponent } from '../user-guide-dialog/user-guide-dialog.component';
import { SiteDataCacheService } from '@shared/services/site-data-cache.service';
import { GroupService } from '@feature/office/services/group.service';
import { OfficeModel } from '@common/models/admin.model';

@Component({
  selector: 'mh-user-profile',
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, SvgIconComponent, RouterLink, RouterLinkActive],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileComponent implements OnDestroy {
  // Injectables
  private authService = inject(GlobalAuthService);
  private cdnRegisteryService = inject(CdnRegisteryService);
  private dialog = inject(Dialog);
  private router = inject(Router);
  private userService = inject(OfficeUserService);
  private rolesService = inject(RolesService);
  private siteDataCache = inject(SiteDataCacheService);
  private groupService = inject(GroupService);

  // Group names looked up by id, for offices whose details don't carry one.
  private groupNames = signal<Record<string, string>>({});

  /** The selected office's group, by name. Empty for a group site, or until it has loaded. */
  protected siteGroupName = computed(() => {
    const site = this.authService.selectedSiteObject();
    if (site?.entityType !== 'office' || !site.id) return '';

    const office = this.siteDataCache.getCachedData(String(site.id), 'office') as OfficeModel | null;
    if (!office?.groupId) return '';

    return this.groupNames()[String(office.groupId)] || office.groupName || '';
  });

  protected authData = computed(() => this.authService.authData());
  protected userProfilePhotoUrl = signal<string | null>(null);
  private userDetails = signal<OfficeUser | null>(null);
  protected userDisplayName = computed(() => {
    const user = this.userDetails();
    if (user) {
      if (user.displayName?.trim()) {
        return user.displayName;
      }

      const composedName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
      if (composedName.length) {
        return composedName;
      }
    }

    const auth = this.authData();
    return auth?.Displayname || auth?.name || 'Unknown User';
  });

  constructor() {
    this.loadUserDetails();
  }

  private loadUserDetails(): void {
    const auth = this.authData();
    if (!auth?.UserId || this.userDetails()) return;

    this.userService
      .getUserById(auth.UserId)
      .pipe(takeUntilDestroyed())
      .subscribe((user) => {
        this.userDetails.set(user);
        this.userProfilePhotoUrl.set(
          user.profilePhotoUrl != null && user.profilePhotoUrl.length > 0 ? user.profilePhotoUrl : null,
        );
      });
  }

  protected getUserRole(): string {
    const user = this.userDetails();
    if (user?.roleName) {
      return this.formatRoleName(user.roleName);
    }

    const auth = this.authData();

    // Check appRole first (for first sign-in), then roles array
    const userAppRole = auth?.AppRole;
    const userRoles = auth?.roles || [];

    if (userAppRole) {
      return this.formatRoleName(userAppRole);
    }

    if (userRoles.length > 0) {
      const firstRole = userRoles[0]; // we will only ever have one role
      return this.formatRoleName(firstRole);
    }

    // roles loaded from access token via GlobalAuthService -> RolesService
    const rolesFromService = this.rolesService.roles();
    if (rolesFromService.length > 0) {
      return this.formatRoleName(rolesFromService[0]);
    }

    return 'User';
  }

  private siteNameCache = signal<string>('');

  // Use computed to make this reactive to changes in selectedSiteObject
  protected getUserSite = computed(() => {
    const selectedSiteObject = this.authService.selectedSiteObject();
    const selectedSiteId = this.authService.selectedSite();
    const cachedName = this.siteNameCache();

    if (selectedSiteObject) {
      // We have the full site object, use it
      return selectedSiteObject.name;
    } else if (selectedSiteId) {
      // We only have the site ID
      if (cachedName) {
        return cachedName;
      } else {
        // Try to load the site name
        this.loadSiteNameForId(selectedSiteId);
        return `Site ID: ${selectedSiteId}`;
      }
    } else {
      return 'No site selected';
    }
  });

  private loadSiteNameForId(siteId: number): void {
    // Avoid multiple simultaneous requests
    if (this.siteNameCache()) return;

    this.authService
      .getSiteById(siteId, this.authService.selectedSiteObject()?.entityType)
      .pipe(
        take(1),
        // One lookup rather than every site just to read a name (PM-546). The
        // type is genuinely unknown here: this runs only when we have a bare id.
        tap((site: ISiteModel | undefined) => {
          if (site) {
            this.siteNameCache.set(site.text);
          }
        }),
      )
      .subscribe();
  }

  private formatRoleName(role: string | number): string {
    if (typeof role === 'string') {
      // e.g. "AccountManager" -> "Account Manager"
      return role.replace(/([A-Z])/g, ' $1').trim();
    }

    // If it's a number, try to map it to RolesEnum
    const roleEntry = Object.entries(RolesEnum).find(([key, value]) => value === role);
    if (roleEntry) {
      return roleEntry[0].replace(/([A-Z])/g, ' $1').trim();
    }

    return 'User';
  }

  // dropdown menu
  protected isMenuOpen = signal(false);
  protected userMenu = signal<IActionNavItemModel[]>([]);

  // icons
  protected userIcon = signal(this.cdnRegisteryService.getMedia('user')?.svg).asReadonly();
  protected settingsIcon = signal(this.cdnRegisteryService.getMedia('settings')?.svg).asReadonly();
  protected switchIcon = signal(this.cdnRegisteryService.getMedia('arrowRepeat')?.svg).asReadonly();
  protected guideIcon = signal(this.cdnRegisteryService.getMedia('featureReferencesRequired')?.svg).asReadonly();
  protected logoutIcon = signal(this.cdnRegisteryService.getMedia('logout')?.svg).asReadonly();

  private _stopRouteWatcher = new Subject<void>();

  ngOnDestroy(): void {
    this._stopRouteWatcher.next();
  }

  protected async logout(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    await this.authService.logout();
  }

  protected openUserGuide(event: MouseEvent): void {
    event.stopPropagation();
    this.toggleMenu(false);

    this.dialog.open<void>(UserGuideDialogComponent, {
      panelClass: ['modal-panel', 'user-guide-modal-panel'],
      backdropClass: 'modal-backdrop',
      autoFocus: false,
      disableClose: false,
      closeOnNavigation: true,
    });
  }

  protected toggleMenu(isOpen?: boolean): void {
    if (typeof isOpen !== 'undefined') {
      this.isMenuOpen.set(isOpen);
    } else {
      this.isMenuOpen.update((val) => !val);
    }

    if (!this.isMenuOpen()) {
      this._stopRouteWatcher.next();
    } else {
      this.watchRouterEvents();
      this.loadSiteGroupName();
    }
  }

  // The office usually comes from the header's cache. A failed group lookup only hides the line.
  private loadSiteGroupName(): void {
    const site = this.authService.selectedSiteObject();
    if (site?.entityType !== 'office' || !site.id) return;

    this.siteDataCache
      .getSiteData(String(site.id), 'office')
      .pipe(
        take(1),
        map((office) => office as OfficeModel | null),
        filter((office): office is OfficeModel => !!office?.groupId && !office.groupName),
        map((office) => String(office.groupId)),
        filter((groupId) => !this.groupNames()[groupId]),
        switchMap((groupId) =>
          this.groupService.getGroupById(groupId).pipe(
            map((response) => ({ groupId, name: response?.value?.name ?? '' })),
            catchError((error) => {
              console.error("Failed to load the selected office's group:", error);
              return of(null);
            }),
          ),
        ),
      )
      .subscribe((group) => {
        if (group?.name) {
          this.groupNames.update((names) => ({ ...names, [group.groupId]: group.name }));
        }
      });
  }

  private watchRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        tap(() => this.toggleMenu(false)),
        first(),
        takeUntil(this._stopRouteWatcher),
      )
      .subscribe();
  }
}
